import { useEffect } from 'react';

import { reconcileScope } from '../services/scope-reconciliation';
import {
  subscribeScopeAnnouncements,
  subscribeTokenWrittenHere,
} from '../services/scope-broadcast';

/**
 * Runs {@link reconcileScope} on every signal that can mean the shared token
 * changed hands (DV-3013 spec §3.6, narrowed by DV-3573).
 *
 * None of the triggers has to be individually reliable, because the token is
 * re-read on every one of them:
 *   - a BroadcastChannel message;
 *   - the `storage` event — the one a login in another tab always fires, since
 *     it writes the token;
 *   - a token written by THIS tab's own refresh, which fires no `storage`
 *     event here — the one way a tab learns its own refresh brought back a
 *     moved root (DV-3421 review round 5, item 1);
 *   - `visibilitychange` / `focus`, for a backgrounded tab that missed both;
 *   - mount, for a tab restored from the bfcache, which has missed every event
 *     by the time this runs.
 *
 * `storage` gets its OWN listener rather than a second branch inside
 * `App.tsx`'s logout handler. The two cannot conflict — `endSession()` removes
 * the token, so `reconcileScope()` answers `'no-claim'` and does nothing while
 * the logout arm redirects — and one concern per listener is cheaper to read
 * than one listener with two unrelated arms.
 *
 * <p>DV-3573 review finding 3: there is no banner and no state. The banner
 * announced "you have been moved to Org Y" when this tab followed another
 * tab's switch; a switch no longer moves the token, so there is nothing to
 * follow or announce. A token taken over by a different user reloads the tab
 * (`reconcileScope()` does it), which needs no UI of its own.
 */
export const useScopeReconciliation = (): void => {
  useEffect(() => {
    // The result is deliberately unused: every outcome is either "nothing to
    // do" or one `reconcileScope` has already acted on itself — `user-changed`
    // and, since DV-3421, `scope-moved`, which both reload, and the three
    // waiting states, which schedule their own re-check. `switching` is the
    // one benign window, and the tab doing the switch reports it for itself.
    const run = () => {
      reconcileScope();
    };

    run();

    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };

    window.addEventListener('storage', run);
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisible);
    const unsubscribe = subscribeScopeAnnouncements(run);
    const unsubscribeTokenWrites = subscribeTokenWrittenHere(run);

    return () => {
      window.removeEventListener('storage', run);
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribe();
      unsubscribeTokenWrites();
    };
  }, []);
};
